import { Component, AfterViewInit, ViewChild, ElementRef, OnDestroy, Input, Renderer2, AfterViewChecked, EventEmitter, Output } from '@angular/core';
import * as Highcharts from 'highcharts';
import { IReportGroups } from 'src/app/interfaces/ireport-groups';
import { IActiveReportGroups } from 'src/app/interfaces/iactive-report-groups';
import { ReportingService } from 'src/app/services/reporting/reporting.service';
import { ResponseTimeService } from 'src/app/services/general/response-time.service';
import { IPrecalPracticeData } from 'src/app/interfaces/iprecal-practice-data';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoService } from '@jsverse/transloco';

interface CustomChartOptions extends Highcharts.Options {
  componentInstance?: ReportingPracticeComponent;
}

@Component({
  selector: 'app-reporting-practice',
  templateUrl: './reporting-practice.component.html',
  styleUrls: ['./reporting-practice.component.scss'],
  standalone: false
})
export class ReportingPracticeComponent implements AfterViewInit, OnDestroy, AfterViewChecked {
  @Output() resultFetched: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() moduleName: string | undefined;
  @Input() moduleUuid: string = '';
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  reportGroups: IReportGroups[] = [];
  isGroupLoading: boolean = true;
  isPreCalLoading: boolean = true;
  showBreadCrums: boolean = false;
  chart: Highcharts.Chart | undefined;
  activeReportGroup: string = '';
  chartDrawn: boolean = false;
  reportGroupUuid: string = '';
  maxColumnWidth = 76; // Maximum width for each column
  precalData: any | undefined;
  globalPointIndex: number = -1;
  activeReportGroups: IActiveReportGroups[] = [];
  selectedReportGroup!: IReportGroups;
  noDataMsg: string = '';
  chartOptions!: Highcharts.Options;
  isReportGroupDataAvailable: boolean = false;
  selectedCta: string = "practice";
  headerText: string = this.translocoService.translate('AVG_PRACTICE_HEADING', {}, 'reporting');

  constructor(private renderer: Renderer2,
    private reportingService: ReportingService,
    private responseTimeService: ResponseTimeService,
    private amplitudeService: AmplitudeService,
    private translationService: TranslationService,
    private translocoService: TranslocoService
  ) { }

  ngAfterViewChecked() {
    if (!this.isGroupLoading && !this.isPreCalLoading && this.chartContainer && !this.chartDrawn && this.isReportGroupDataAvailable) {
      this.drawChart();
      this.chartDrawn = true;
    }
  }

  ngOnInit() {
    // Sending the Event of Clicking 'Practices' Tab to Amplitude
    this.amplitudeService.trackEvent('modulereport_click_practices', {
      'chart_title':this.headerText
    });

    this.chartOptions = this.getInitialChartOptions();
    this.getReportGroups();
  }

  ngAfterViewInit() {
    this.drawChart();
    this.renderer.listen('document', 'click', (event) => {
      if (event.target && (event.target as HTMLElement).classList.contains('expand-group')) {
        if (this.globalPointIndex > -1) {
          this.onPointClick(this.chart!.series[0].data[+this.globalPointIndex].index);
        }
      }
    });
    // Add event listener for window resize to hide tooltip
    window.addEventListener('resize', () => {
      if (this.chart && this.chart.tooltip) {
        this.chart.tooltip.destroy();
      }
    });
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }

  getInitialChartOptions(): CustomChartOptions {
    return {
      chart: {
        type: 'column',
        events: {
          load: this.adjustColumnWidth.bind(this),
          redraw: this.adjustColumnWidth.bind(this),
          click: () => this.onChartClick()
        }
      },
      title: { text: '' },
      xAxis: { categories: [] },
      yAxis: {
        min: 0,
        max: 100,
        title: { text: '' },
        labels: { format: '' },
        tickPositions: [0, 25, 50, 75, 100],
      },
      legend: { enabled: false },
      plotOptions: {
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: (event) => this.onPointClick(event.point.index as number),
              mouseOver: (event) => this.onPointHover(event as Highcharts.PointerEventObject)
            }
          }
        },
        column: {
          pointPadding: 0.1,
          groupPadding: 0.1,
          maxPointWidth: this.maxColumnWidth
        }
      },
      tooltip: {
        useHTML: true,
        shared: true,
        stickOnContact: true,
        outside: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const points = this.points as Highcharts.TooltipFormatterContextObject[];
          const firstPoint = points && points.length > 0 ? points[0] : null;
          const pointIndex = firstPoint ? firstPoint.point.index : -2;

          // Access the precalData attached to the chart instance
          const chart = firstPoint?.series.chart;
          const precalData = (chart as any).precalData;
          const childData = precalData?.children?.[pointIndex];
          const totalUserAssignmentCount = childData ? childData.totalUserAssignmentCount : 0;
          const practiceCount = childData ? childData.totalPracticeCount : 0;
          const avgPracticeCount = childData ? childData.avgPracticeCount : 0;
          const avgScore = childData ? childData.avgScore : 0;
          let tooltipBody = '';
          const selectedCta = (chart?.options as any).componentInstance.selectedCta;
          const translationService = (chart?.options as any).componentInstance.translocoService;
          const locale = translationService.getActiveLang?.() || 'en';
          const formattedAvgScore = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(avgScore);
          const formattedAvgPracticeCount = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(avgPracticeCount);

          if (selectedCta === 'practice') {
            tooltipBody = `
              <div class="tooltip-body">
                  <b>${formattedAvgPracticeCount}</b> ${translationService.translate('AVG_PRACTICE_HEADING', {}, 'reporting')}
                </div>
                <div class="body-content">
                  <b>${practiceCount}</b> ${translationService.translate('TOTAL_LABEL', {}, 'reporting')} ${practiceCount === 1 ? translationService.translate('PRACTICE', {}, 'reporting') : translationService.translate('PRACTICES', {}, 'reporting')}
                </div>
              </div>`;
          } else if (selectedCta === 'score') {
            tooltipBody = `
              <div class="tooltip-body">
                <div class="body-content">
                  <b>${formattedAvgScore}</b> ${translationService.translate('AVERAGE_SCORE_LABEL', {}, 'reporting')}
                </div>
              </div>`;
          }

          const expandGroupLink = childData?.hasChild ? `
            <div class="link expand-group">
              ${translationService.translate('EXPAND_GROUP', {}, 'reporting')} <img class="expand-group" src="/assets/icons/expand-group.svg" alt="${translationService.translate('EXPAND_GROUP', {}, 'reporting')}" />
            </div>` : '';

          // Remove asterisk from this.x
          let displayString = this.x;
          if (typeof displayString === 'string') {
            displayString = displayString.replace('*', '');
          }

          return `<div id="custom-tooltip" class="custom-tooltip">
                    <div class="tooltip-arrow"></div>
                    <div class="tooltip-content">
                      <div class="tooltip-header">
                        <b>${displayString}</b>
                        <div class="learner-info">${totalUserAssignmentCount} ${totalUserAssignmentCount === 1 ? translationService.translate('USER', {}, 'reporting') : translationService.translate('USERS', {}, 'reporting')}</div>
                      </div>
                      ${tooltipBody}
                      ${expandGroupLink}
                    </div>
                  </div>`;
        },
        positioner: function (labelWidth, labelHeight, point) {
          const chart = this.chart;
          const yOffset = 10;
          const yPositionAbove = point.plotY + chart.plotTop - labelHeight - yOffset;
          const yPosition = yPositionAbove;

          return {
            x: point.plotX + chart.plotLeft - labelWidth / 2,
            y: yPosition,
          };
        },
        backgroundColor: 'white',
        borderColor: 'white',
        borderRadius: 5,
        borderWidth: 1,
        shadow: true,
      },
      credits: { enabled: false },
      accessibility: {
        enabled: false,
      },
      componentInstance: this

    };
  }

  handleExpandGroupClick(pointIndex: number) {
    if (this.chart) {
      const point = this.chart.series[0].data[pointIndex];
      this.onPointClick(point.index);
    }
  }

  onPointHover(point: any) {
    const pointIndex = point.target.index;
    this.globalPointIndex = pointIndex;
  }

  setGroup(group: IReportGroups) {
    this.reportGroups.forEach(g => g.active = g.reportGroupName === group.reportGroupName);
    this.setActiveReportGroup(group);
  }

  setActiveReportGroup(reportGroup: IReportGroups) {
    this.activeReportGroup = reportGroup.reportGroupName;
    this.reportGroupUuid = reportGroup.reportGroupUuid;
    this.activeReportGroups = [];
    this.activeReportGroups.push({
      reportGroupName: reportGroup.reportGroupName,
      reportGroupUuid: reportGroup.reportGroupUuid,
      active: true,
      nestedGroups: []
    });
    this.shouldShowBreadcrums();
    if (this.isReportGroupDataAvailable) {
      this.reportGroupChildData();
    }
  }

  getReportGroups() {
    // Account UUID
    const selectedAccountInfo = localStorage.getItem('selected_account_info');
    const currentAccountUuid = localStorage.getItem('current_account_uuid');
    const accountUuid = selectedAccountInfo ? JSON.parse(selectedAccountInfo).uuid : currentAccountUuid;
    //
    this.noDataMsg = '';
    this.isReportGroupDataAvailable = true;
    this.responseTimeService.logResponseTime(
      this.reportingService.getReportGroups(accountUuid, this.moduleUuid),
      'getReportGroups'
    ).subscribe({
      next: (res: IReportGroups[]) => {
        this.isGroupLoading = false;
        if (!res || Object.keys(res).length === 0) {
          this.noDataMsg = this.translocoService.translate('NO_DATA_FOUND', {}, 'reporting');
          this.isReportGroupDataAvailable = false;
          this.isPreCalLoading = false;
        }
        this.reportGroups = res;
        this.reportGroups.forEach(group => {
          group.active = group.reportSequence === 1;
          group.active ? this.setActiveReportGroup(group) : '';
        });
        this.selectedReportGroup = this.reportGroups.find(group => group.active) || this.reportGroups[0];
      },
      error: (error) => {
        console.error('Error fetching report groups', error);
        this.isGroupLoading = false;
        this.noDataMsg = error.error.message;
      }
    });
  }

  reportGroupChildData() {
    this.isPreCalLoading = true;
    this.noDataMsg = '';
    // Account UUID
    const selectedAccountInfo = localStorage.getItem('selected_account_info');
    const currentAccountUuid = localStorage.getItem('current_account_uuid');
    const accountUuid = selectedAccountInfo ? JSON.parse(selectedAccountInfo).uuid : currentAccountUuid;
    //
    this.responseTimeService.logResponseTime(
      this.reportingService.getPrecalcPracticeReportGroupChildData(accountUuid, this.moduleUuid, this.reportGroupUuid),
      'getPrecalcPracticeReportGroupChildData'
    ).subscribe({
      next: (res: IPrecalPracticeData) => {
        this.isPreCalLoading = false;
        if (!res || Object.keys(res).length === 0 || res?.children === null) {
          this.noDataMsg = this.translocoService.translate('NO_DATA_FOUND', {}, 'reporting');
        }
        this.precalData = res;
        this.updateChartOptions();
        this.resultFetched.emit(true);
      },
      error: (error) => {
        this.isPreCalLoading = false;
        console.error('Error fetching report groups', error);
        this.noDataMsg = error;
        this.updateChartOptions();
        this.resultFetched.emit(true);
      }
    });
  }

  updateChartOptions() {
    if (this.precalData && this.precalData.children?.length > 0) {
      this.chartOptions.xAxis = {
        categories: this.precalData.children.map((child: any) =>
          child.hasChild ? `${child.displayString}*` : child.displayString
        )
      };

      if (this.selectedCta === 'practice') {
        this.setPracticeChartOptions();
      } else if (this.selectedCta === 'score') {
        this.setScoreChartOptions();
      }
    } else {
      this.setEmptyChartOptions();
    }
    this.drawChart();
  }

  setPracticeChartOptions() {
    const maxPracticeCount = Math.max(...this.precalData.children.map((child: any) => child.avgPracticeCount));

    // Calculate yAxisMax, ensuring it is the next multiple of 5 greater than maxPracticeCount
    let yAxisMax = Math.ceil(maxPracticeCount / 5) * 5;
    console.log("yAxisMax", yAxisMax)

    // Ensure yAxisMax is strictly greater than maxPracticeCount
    if (yAxisMax <= maxPracticeCount) {
      console.log("yAxisMax <= maxPracticeCount", yAxisMax, maxPracticeCount)
      yAxisMax += 5;
    }

    // Calculate tickInterval based on the adjusted yAxisMax
    let tickInterval = Math.ceil(yAxisMax / 6 / 5) * 5;
    // / Adjust tickInterval to ensure it aligns with yAxisMax
    if (yAxisMax % tickInterval !== 0) {
      tickInterval = Math.ceil(yAxisMax / 5);
    }
    if (yAxisMax <= 5) {
      tickInterval = 1
    } else if (yAxisMax <= 10) {
      tickInterval = 2
    } else if (yAxisMax <= 15) {
      tickInterval = 3
    } else if (yAxisMax <= 20) {
      tickInterval = 4
    }

    // Update chart options
    this.chartOptions.series = [
      this.createSeries(
        'avgPracticeCount',
        this.precalData.children.map((child: any) => child.avgPracticeCount),
        'rgba(55, 150, 164, 0.6)',
        'rgba(55, 150, 164, 1)'
      )
    ];

    this.chartOptions.yAxis = {
      min: 0,
      max: yAxisMax,
      title: { text: '' },
      labels: { format: '{value}' },
      tickInterval: tickInterval,
    };
  }


  setScoreChartOptions() {
    this.chartOptions.series = [
      this.createSeries('avgScore', this.precalData.children.map((child: any) => child.avgScore), 'rgba(55, 150, 164, 0.6)', 'rgba(55, 150, 164, 1)')
    ];

    this.chartOptions.yAxis = {
      min: 0,
      max: 5,
      title: { text: '' },
      labels: { format: '{value}' },
      tickInterval: 1
    };
  }

  setEmptyChartOptions() {
    this.chartOptions.xAxis = {
      categories: []
    };
    this.chartOptions.series = [
      this.createSeries('avgPracticeCount', [], 'rgba(55, 150, 164, 0.6)', 'rgba(55, 150, 164, 1)')
    ];
  }

  createSeries(name: string, data: number[], color: string, hoverColor: string): Highcharts.SeriesColumnOptions {
    return {
      name,
      data,
      color,
      type: 'column',
      states: {
        hover: {
          color: hoverColor
        }
      }
    } as Highcharts.SeriesColumnOptions;
  }

  drawChart() {
    const scrollPosition = window.scrollY;
    if (this.chartContainer) {
      this.chart = Highcharts.chart(this.chartContainer.nativeElement, this.chartOptions) as Highcharts.Chart;
      (this.chart as any).precalData = this.precalData;
      if (this.chart && this.chart.series) {
        console.log("chart found", this.chart.series.length)
        this.chart.series.forEach(series => {
          if (series.points) {
            series.points.forEach(point => {
              const childData = this.precalData?.children?.[point.index];
              if (childData && childData.hasChild) {
                point.graphic?.attr({ cursor: 'pointer' });
              } else {
                point.graphic?.attr({ cursor: 'default' });
              }
            });
          }
        });
      }
    } else {
      console.log("chart not found");
    }
    window.scrollTo(0, scrollPosition);
  }

  adjustColumnWidth() {
    if (this.chart && this.chartOptions.xAxis) {
      const xAxisOptions = this.chartOptions.xAxis as Highcharts.XAxisOptions;
      const chartWidth = this.chart.plotWidth;
      const numCategories = xAxisOptions.categories?.length || 0;

      if (this.chart.series) {
        const pointWidth = Math.min(this.maxColumnWidth, chartWidth / numCategories - 10);

        let needsRedraw = false;
        this.chart.series.forEach(series => {
          const seriesOptions = series.options as Highcharts.SeriesColumnOptions;
          if (seriesOptions.pointWidth !== pointWidth) {
            series.update({
              type: 'column',
              pointWidth: pointWidth > this.maxColumnWidth ? this.maxColumnWidth : pointWidth
            }, false);
            needsRedraw = true;
          }
        });

        if (needsRedraw) {
          this.chart.redraw();
        }
      }
    }
  }

  onPointClick(index: number) {
    const selectedGroup = this.reportGroups.find(group => group.reportGroupUuid === this.reportGroupUuid);
    if (selectedGroup) {
      this.selectedReportGroup = selectedGroup;
    }
    const childData = this.precalData?.children?.[index];
    // Sending the Event of Clicking on a Practice Bar to Amplitude
    this.amplitudeService.trackEvent('modulereport_click_practicesbar', {
      'group_name': childData?.displayString,
    });

    if (childData && childData.hasChild && !this.isPreCalLoading) {
      this.reportGroupUuid = childData.reportGroupUuid;
      const activeGroup = this.activeReportGroups.find(group => group.active);
      if (activeGroup) {
        activeGroup.nestedGroups?.push({
          reportGroupName: childData.displayString,
          reportGroupUuid: childData.reportGroupUuid,
          active: true,
          reportSequence: 1
        });
      }
      this.shouldShowBreadcrums();
      this.reportGroupChildData();
    }

  }

  onChartClick() {
    this.chart?.tooltip.hide(0);
  }

  shouldShowGroup(): boolean {
    return this.precalData?.children?.some((child: any) => child.hasChild) ?? false;
  }

  shouldShowBreadcrums(): boolean {
    this.showBreadCrums = this.activeReportGroups.some(group => group.nestedGroups && group.nestedGroups.length > 0);
    return this.showBreadCrums;
  }

  resetGroup(reportGroupUuid: string) {
    const selectedGroup = this.reportGroups.find(group => group.reportGroupUuid === reportGroupUuid);
    if (selectedGroup) {
      this.selectedReportGroup = selectedGroup;
      this.setGroup(selectedGroup);
    }
  }

  pruneNestedGroup(group: IActiveReportGroups) {
    this.reportGroupUuid = group.reportGroupUuid;
    this.reportGroupChildData();
    const nestedGroups = this.activeReportGroups[0].nestedGroups;
    if (nestedGroups) {
      const index = nestedGroups.findIndex(g => g.reportGroupUuid === group.reportGroupUuid);
      if (index !== -1) {
        nestedGroups.splice(index + 1);
      }
    }
  }

  formatNumber(value?: number): string {
    if (!value) {
      return '';
    }
    const truncatedValue = Math.floor(value * 10) / 10;
    return truncatedValue.toFixed(1);
  }
  setCta(cta: string) {
    this.selectedCta = cta;

    switch (this.selectedCta) {
      case 'practice':
        this.headerText = this.translocoService.translate('AVG_PRACTICE_HEADING', {}, 'reporting');

        // Sending the Event of Clicking Practices 'Avg. Practices' Toggle to Amplitude
        this.amplitudeService.trackEvent('modulereport_click_practicestogglesessions', {
          'chart_title': this.headerText
        });
        break;

      case 'score':
        this.headerText = this.translocoService.translate('AVG_SCORE_HEADING', {}, 'reporting');

        // Sending the Event of Clicking Practices 'Avg. Score' Toggle to Amplitude
        this.amplitudeService.trackEvent('modulereport_click_practicestogglescore', {
          'chart_title': this.headerText
        });
        break;
    }

    this.updateChartOptions();
  }

}
